import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { format, addDays, startOfDay } from "date-fns";
import jsPDF from "jspdf";
import {
  Loader2, LogOut, Pencil, ExternalLink, Check, Inbox, CreditCard,
  Phone, Mail, MessageSquare, Filter, CalendarDays, Lock, Unlock, Ban, Wand2,
  FileSignature, Trash2, X, Stethoscope, ShieldCheck,
} from "lucide-react";
import { Calendar } from "../components/ui/calendar";
import { useAuth } from "../context/AuthContext";
import { API } from "../context/SiteContext";

const STATUSES = [
  { id: "all", label: "All" },
  { id: "new", label: "New" },
  { id: "contacted", label: "Contacted" },
  { id: "scheduled", label: "Scheduled" },
  { id: "archived", label: "Archived" },
];

const STATUS_COLORS = {
  new: "bg-[#9F5C45] text-[#FDFBF7]",
  contacted: "bg-[#3A5A40] text-[#FDFBF7]",
  scheduled: "bg-[#2C4C3B] text-[#FDFBF7]",
  archived: "bg-[#EAE5D9] text-[#57534E]",
};

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
};

const money = (cents, currency = "usd") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: currency.toUpperCase() }).format(
    typeof cents === "number" && cents > 1000 ? cents / 100 : cents
  );

export default function AdminDashboard() {
  const { user, checking, logout, authHeader } = useAuth();
  const [tab, setTab] = useState("enrollments");
  const [enrollments, setEnrollments] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [consentForms, setConsentForms] = useState([]);
  const [activeConsent, setActiveConsent] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [activeReferral, setActiveReferral] = useState(null);
  const [privacyAcks, setPrivacyAcks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!user) return;
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const [e, t, c, r, p] = await Promise.all([
          axios.get(`${API}/enrollments`, { headers: authHeader() }),
          axios.get(`${API}/payments/transactions`, { headers: authHeader() }),
          axios.get(`${API}/admin/consent-forms`, { headers: authHeader() }).catch(() => ({ data: [] })),
          axios.get(`${API}/admin/referrals`, { headers: authHeader() }).catch(() => ({ data: [] })),
          axios.get(`${API}/admin/privacy-acks`, { headers: authHeader() }).catch(() => ({ data: [] })),
        ]);
        if (!mounted) return;
        setEnrollments(e.data || []);
        setTransactions(t.data?.transactions || []);
        setConsentForms(c.data || []);
        setReferrals(r.data || []);
        setPrivacyAcks(p.data || []);
      } catch (err) {
        console.error(err);
        toast.error("Could not load dashboard data.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const filtered = useMemo(() => {
    return enrollments.filter((e) => {
      if (filter !== "all" && e.status !== filter) return false;
      if (search) {
        const q = search.toLowerCase();
        if (
          !(e.full_name || "").toLowerCase().includes(q) &&
          !(e.email || "").toLowerCase().includes(q) &&
          !(e.phone || "").toLowerCase().includes(q) &&
          !(e.concern || "").toLowerCase().includes(q)
        ) return false;
      }
      return true;
    });
  }, [enrollments, filter, search]);

  const counts = useMemo(() => {
    const c = { all: enrollments.length, new: 0, contacted: 0, scheduled: 0, archived: 0 };
    enrollments.forEach((e) => { c[e.status] = (c[e.status] || 0) + 1; });
    return c;
  }, [enrollments]);

  const paidTotal = useMemo(
    () => transactions.filter((t) => t.payment_status === "paid").reduce((s, t) => s + (t.amount || 0), 0),
    [transactions]
  );

  const updateStatus = async (id, status) => {
    try {
      const { data } = await axios.patch(
        `${API}/enrollments/${id}`,
        { status },
        { headers: authHeader() }
      );
      setEnrollments((es) => es.map((e) => (e.id === id ? data : e)));
      toast.success(`Marked as ${status}.`);
    } catch (e) {
      console.error(e);
      toast.error("Could not update status.");
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <Loader2 className="w-6 h-6 animate-spin text-[#3A5A40]" />
      </div>
    );
  }
  if (!user) return <Navigate to="/admin" replace />;

  return (
    <main className="min-h-screen bg-[#F4F1EA] pb-24" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-[#FDFBF7]/90 backdrop-blur-xl border-b border-[#E2DCD0]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="font-serif text-xl text-[#1C1917]">Supervisor Dashboard</span>
            <span className="hidden sm:inline-block text-xs uppercase tracking-[0.2em] text-[#9F5C45] border border-[#9F5C45]/30 px-2 py-1">
              {user.email}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/admin/editor" data-testid="admin-nav-editor" className="inline-flex items-center gap-2 px-4 py-2 btn-ghost text-xs tracking-wide">
              <Pencil className="w-3.5 h-3.5" /> Edit site
            </Link>
            <Link to="/video" data-testid="admin-nav-scripts" className="hidden md:inline-flex items-center gap-2 px-4 py-2 btn-ghost text-xs tracking-wide">
              <Wand2 className="w-3.5 h-3.5" /> Video scripts
            </Link>
            <button
              onClick={() => buildAuditPdf({ enrollments, transactions, consentForms, referrals, privacyAcks })}
              data-testid="admin-audit-pdf"
              className="hidden md:inline-flex items-center gap-2 px-4 py-2 btn-ghost text-xs tracking-wide"
            >
              <FileSignature className="w-3.5 h-3.5" /> Audit PDF
            </button>
            <Link to="/" data-testid="admin-view-site" className="hidden sm:inline-flex items-center gap-2 px-4 py-2 btn-ghost text-xs tracking-wide">
              View site <ExternalLink className="w-3.5 h-3.5" />
            </Link>
            <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 px-4 py-2 btn-ghost text-xs tracking-wide">
              <LogOut className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-12 md:pt-16 space-y-10">
        {/* Stat strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Total enrollments" value={counts.all} icon={Inbox} />
          <Stat label="Awaiting outreach" value={counts.new || 0} icon={Inbox} tone="accent" />
          <Stat label="Sessions paid" value={transactions.filter(t => t.payment_status === "paid").length} icon={CreditCard} />
          <Stat label="Revenue (paid)" value={money(paidTotal)} icon={CreditCard} tone="dark" />
          <Stat label="Signed consents" value={consentForms.length} icon={FileSignature} />
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-[#E2DCD0] flex-wrap">
          {[
            { id: "enrollments", label: `Enrollments (${enrollments.length})` },
            { id: "schedule", label: "Schedule" },
            { id: "payments", label: `Payments (${transactions.length})` },
            { id: "consent", label: `Consent forms (${consentForms.length})` },
            { id: "referrals", label: `Referrals (${referrals.length})` },
            { id: "privacy", label: `Privacy acks (${privacyAcks.length})` },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`dash-tab-${t.id}`}
              className={`px-5 py-3 text-sm tracking-wide transition-colors border-b-2 -mb-px ${
                tab === t.id
                  ? "border-[#3A5A40] text-[#1C1917]"
                  : "border-transparent text-[#57534E] hover:text-[#1C1917]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#3A5A40]" /></div>
        ) : tab === "enrollments" ? (
          <EnrollmentsTab
            list={filtered}
            counts={counts}
            filter={filter}
            setFilter={setFilter}
            search={search}
            setSearch={setSearch}
            updateStatus={updateStatus}
          />
        ) : tab === "schedule" ? (
          <ScheduleTab authHeader={authHeader} />
        ) : tab === "consent" ? (
          <ConsentFormsTab
            list={consentForms}
            onOpen={setActiveConsent}
            onDelete={async (id) => {
              if (!window.confirm("Delete this consent submission?")) return;
              try {
                await axios.delete(`${API}/admin/consent-forms/${id}`, { headers: authHeader() });
                setConsentForms((prev) => prev.filter((c) => c.id !== id));
                toast.success("Consent submission deleted.");
              } catch (err) {
                console.error(err);
                toast.error("Could not delete.");
              }
            }}
          />
        ) : tab === "referrals" ? (
          <ReferralsTab
            list={referrals}
            onOpen={setActiveReferral}
            onDelete={async (id) => {
              if (!window.confirm("Delete this referral?")) return;
              try {
                await axios.delete(`${API}/admin/referrals/${id}`, { headers: authHeader() });
                setReferrals((prev) => prev.filter((r) => r.id !== id));
                toast.success("Referral deleted.");
              } catch (err) {
                console.error(err);
                toast.error("Could not delete.");
              }
            }}
            onStatus={async (id, status) => {
              try {
                const res = await axios.patch(`${API}/admin/referrals/${id}`, { status }, { headers: authHeader() });
                setReferrals((prev) => prev.map((r) => (r.id === id ? res.data : r)));
                toast.success(`Marked ${status}`);
              } catch (err) {
                console.error(err);
                toast.error("Could not update status.");
              }
            }}
          />
        ) : tab === "privacy" ? (
          <PrivacyAcksTab
            list={privacyAcks}
            onDelete={async (id) => {
              if (!window.confirm("Delete this privacy acknowledgment?")) return;
              try {
                await axios.delete(`${API}/admin/privacy-acks/${id}`, { headers: authHeader() });
                setPrivacyAcks((prev) => prev.filter((p) => p.id !== id));
                toast.success("Acknowledgment deleted.");
              } catch (err) {
                console.error(err);
                toast.error("Could not delete.");
              }
            }}
          />
        ) : (
          <PaymentsTab list={transactions} />
        )}
      </div>
      {activeConsent && (
        <ConsentDetailModal
          submission={activeConsent}
          onClose={() => setActiveConsent(null)}
        />
      )}
      {activeReferral && (
        <ReferralDetailModal
          referral={activeReferral}
          onClose={() => setActiveReferral(null)}
        />
      )}
    </main>
  );
}

// ----- Subcomponents -----
const Stat = ({ label, value, icon: Icon, tone }) => {
  const cls = tone === "dark"
    ? "bg-[#2C4C3B] text-[#FDFBF7]"
    : tone === "accent"
    ? "bg-[#9F5C45] text-[#FDFBF7]"
    : "bg-white border border-[#E2DCD0] text-[#1C1917]";
  return (
    <div className={`p-6 ${cls}`} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between mb-3">
        <span className={`overline ${tone ? "opacity-80" : "text-[#57534E]"}`}>{label}</span>
        <Icon className="w-4 h-4 opacity-70" strokeWidth={1.5} />
      </div>
      <div className="font-serif text-3xl md:text-4xl">{value}</div>
    </div>
  );
};

const EnrollmentsTab = ({ list, counts, filter, setFilter, search, setSearch, updateStatus }) => (
  <div className="space-y-6">
    <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
      <div className="flex flex-wrap items-center gap-1 bg-white border border-[#E2DCD0] p-1">
        {STATUSES.map((s) => (
          <button
            key={s.id}
            onClick={() => setFilter(s.id)}
            data-testid={`filter-${s.id}`}
            className={`px-3 py-2 text-xs tracking-wide transition-colors ${
              filter === s.id
                ? "bg-[#3A5A40] text-[#FDFBF7]"
                : "text-[#57534E] hover:bg-[#F4F1EA]"
            }`}
          >
            {s.label} {s.id !== "all" && counts[s.id] != null ? `(${counts[s.id]})` : ""}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2 bg-white border border-[#E2DCD0] px-4 py-2 sm:w-80">
        <Filter className="w-4 h-4 text-[#57534E]" />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, email, phone..."
          data-testid="dash-search"
          className="bg-transparent outline-none flex-1 text-sm placeholder:text-[#A8A29E]"
        />
      </div>
      {list.length > 0 && (
        <button
          onClick={() =>
            downloadCsv(
              `letgo-enrollments-${new Date().toISOString().slice(0, 10)}.csv`,
              list.map((e) => ({
                id: e.id,
                created_at: e.created_at,
                status: e.status,
                name: e.name,
                email: e.email,
                phone: e.phone,
                reason: e.reason,
                service: e.service,
                message: e.message,
              }))
            )
          }
          data-testid="enrollments-export-csv"
          className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.22em] border border-[#3A5A40] text-[#3A5A40] hover:bg-[#3A5A40] hover:text-white transition-colors"
        >
          Export CSV
        </button>
      )}
    </div>

    {list.length === 0 ? (
      <div className="bg-white border border-[#E2DCD0] p-16 text-center">
        <p className="font-serif italic text-2xl text-[#57534E]">No enrollments here yet.</p>
      </div>
    ) : (
      <div className="space-y-3">
        {list.map((e) => (
          <article key={e.id} data-testid={`enroll-row-${e.id}`} className="bg-white border border-[#E2DCD0] p-6 md:p-8 grid md:grid-cols-[1fr_auto] gap-6 items-start">
            <div className="space-y-3">
              <div className="flex flex-wrap items-baseline gap-3">
                <h3 className="font-serif text-2xl text-[#1C1917]">{e.full_name}</h3>
                <span className={`overline px-2 py-1 ${STATUS_COLORS[e.status] || STATUS_COLORS.new}`}>
                  {e.status}
                </span>
                <span className="text-xs text-[#57534E]">{fmt(e.created_at)}</span>
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#57534E]">
                <a href={`mailto:${e.email}`} className="inline-flex items-center gap-2 hover:text-[#3A5A40]">
                  <Mail className="w-3.5 h-3.5" /> {e.email}
                </a>
                <a href={`tel:${e.phone}`} className="inline-flex items-center gap-2 hover:text-[#3A5A40]">
                  <Phone className="w-3.5 h-3.5" /> {e.phone}
                </a>
                <span className="inline-flex items-center gap-2"><MessageSquare className="w-3.5 h-3.5" /> Prefers {e.preferred_contact}</span>
              </div>
              <div className="text-sm">
                <span className="overline text-[#9F5C45] mr-2">Concern</span>
                <span className="text-[#1C1917]">{e.concern}</span>
                {e.age_range && <span className="text-[#57534E]"> · {e.age_range}</span>}
              </div>
              {e.insurance_provider && (
                <div className="text-sm text-[#57534E]">
                  <span className="overline text-[#9F5C45] mr-2">Insurance</span>
                  {e.insurance_provider}{e.insurance_member_id ? ` · #${e.insurance_member_id}` : ""}
                </div>
              )}
              {e.message && (
                <p className="text-sm text-[#1C1917] bg-[#F4F1EA] p-4 border-l-2 border-[#9F5C45] italic font-serif">
                  &ldquo;{e.message}&rdquo;
                </p>
              )}
            </div>
            <div className="flex md:flex-col gap-2 md:items-end">
              {["new", "contacted", "scheduled", "archived"].filter((s) => s !== e.status).map((s) => (
                <button
                  key={s}
                  onClick={() => updateStatus(e.id, s)}
                  data-testid={`enroll-${e.id}-mark-${s}`}
                  className="inline-flex items-center gap-2 px-4 py-2 text-xs tracking-wide border border-[#E2DCD0] hover:bg-[#F4F1EA] transition-colors text-[#1C1917]"
                >
                  <Check className="w-3 h-3" /> {s}
                </button>
              ))}
              <a
                href={`mailto:${e.email}?${new URLSearchParams({
                  subject: "Let Go Wellness · your intake paperwork",
                  body:
`Hi ${(e.name || "there").split(" ")[0]},\n\nThanks for reaching out to Let Go Wellness. To save time at your first session, please complete your intake paperwork here:\n\n${window.location.origin}/intake?name=${encodeURIComponent(e.name || "")}\n\nIt's two short forms — consent for services and HIPAA privacy practices. About five minutes total. You'll get a signed PDF of each for your records.\n\nAny questions, call the clinic at 203-437-8193.\n\nWarmly,\nLet Go Wellness`
                }).toString().replace(/\+/g, "%20")}`}
                data-testid={`enroll-${e.id}-send-intake`}
                className="inline-flex items-center gap-2 px-4 py-2 text-xs tracking-wide bg-[#3A5A40] hover:bg-[#243B2C] text-white transition-colors"
              >
                <Mail className="w-3 h-3" /> Send intake link
              </a>
              {e.phone && (
                <a
                  href={`sms:${e.phone.replace(/[^0-9+]/g, "")}?body=${encodeURIComponent(
                    `Hi ${(e.name || "there").split(" ")[0]}, this is Let Go Wellness. Please complete your intake forms (about 5 min) here before your first session: ${window.location.origin}/intake?name=${encodeURIComponent(e.name || "")}`
                  )}`}
                  data-testid={`enroll-${e.id}-text-intake`}
                  className="inline-flex items-center gap-2 px-4 py-2 text-xs tracking-wide border border-[#3A5A40] text-[#3A5A40] hover:bg-[#3A5A40] hover:text-white transition-colors"
                >
                  <MessageSquare className="w-3 h-3" /> Text intake link
                </a>
              )}
            </div>
          </article>
        ))}
      </div>
    )}
  </div>
);

const PaymentsTab = ({ list }) => (
  <div className="space-y-3">
    {list.length === 0 ? (
      <div className="bg-white border border-[#E2DCD0] p-16 text-center">
        <p className="font-serif italic text-2xl text-[#57534E]">No payments yet.</p>
      </div>
    ) : (
      <div className="bg-white border border-[#E2DCD0] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F4F1EA] text-[#57534E]">
            <tr className="text-left">
              <th className="overline px-6 py-4">Date</th>
              <th className="overline px-6 py-4">Package</th>
              <th className="overline px-6 py-4">Patient</th>
              <th className="overline px-6 py-4">Amount</th>
              <th className="overline px-6 py-4">Status</th>
            </tr>
          </thead>
          <tbody>
            {list.map((t) => (
              <tr key={t.session_id} data-testid={`pay-row-${t.session_id}`} className="border-t border-[#E2DCD0]">
                <td className="px-6 py-4 text-[#57534E] whitespace-nowrap">{fmt(t.created_at)}</td>
                <td className="px-6 py-4 text-[#1C1917]">{t.package_name}</td>
                <td className="px-6 py-4 text-[#57534E]">{t.patient_email || "—"}</td>
                <td className="px-6 py-4 font-serif text-[#1C1917]">${(t.amount || 0).toFixed(2)}</td>
                <td className="px-6 py-4">
                  <span className={`overline px-2 py-1 ${
                    t.payment_status === "paid" ? "bg-[#3A5A40] text-[#FDFBF7]" :
                    t.payment_status === "pending" ? "bg-[#EAE5D9] text-[#2C4C3B]" :
                    "bg-[#9F5C45]/15 text-[#9F5C45]"
                  }`}>
                    {t.payment_status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);


const fmtTime = (hhmm) => {
  const [h, m] = hhmm.split(":").map(Number);
  const hour12 = ((h + 11) % 12) + 1;
  const ampm = h < 12 ? "AM" : "PM";
  return `${hour12}:${m.toString().padStart(2, "0")} ${ampm}`;
};

// ─────────────────────────────────────────────────────────────
// Audit-ready PDF — all admin data in one dated report. Useful
// for compliance snapshots, quarterly reviews or auditor requests.
// ─────────────────────────────────────────────────────────────
function buildAuditPdf({ enrollments = [], transactions = [], consentForms = [], referrals = [], privacyAcks = [] }) {
  const pdf = new jsPDF({ unit: "in", format: "letter" });
  const W = 8.5;
  const M = 0.6;
  let y = 0.8;

  const line = () => { pdf.setDrawColor(180, 170, 150); pdf.setLineWidth(0.005); pdf.line(M, y, W - M, y); y += 0.25; };
  const pageBreakIf = (need = 1) => { if (y + need > 10.5) { pdf.addPage(); y = 0.8; } };

  pdf.setFont("times", "bold"); pdf.setFontSize(20);
  pdf.text("Let Go Wellness", M, y); y += 0.3;
  pdf.setFont("times", "italic"); pdf.setFontSize(11);
  pdf.setTextColor(120, 115, 105);
  pdf.text(`Compliance & Ops Audit Report — generated ${new Date().toLocaleString()}`, M, y);
  pdf.setTextColor(28, 25, 23);
  y += 0.35;
  line();

  const section = (title, count) => {
    pageBreakIf(1);
    pdf.setFont("helvetica", "bold"); pdf.setFontSize(11);
    pdf.setTextColor(58, 90, 64);
    pdf.text(`${title} (${count})`, M, y);
    pdf.setTextColor(28, 25, 23);
    y += 0.28;
  };
  const kv = (label, value) => {
    pageBreakIf(0.3);
    pdf.setFont("helvetica", "bold"); pdf.setFontSize(9);
    pdf.text(label, M, y);
    pdf.setFont("helvetica", "normal");
    const lines = pdf.splitTextToSize(String(value || "—"), W - 2 * M - 1.5);
    pdf.text(lines, M + 1.5, y);
    y += 0.18 * Math.max(1, lines.length);
  };
  const rowDivider = () => { pdf.setDrawColor(220, 210, 195); pdf.setLineWidth(0.003); pdf.line(M, y, W - M, y); y += 0.18; };

  // ── Summary ──
  section("Summary", "");
  y -= 0.05;
  kv("Total enrollments", enrollments.length);
  kv("Paid transactions", transactions.filter((t) => t.payment_status === "paid").length);
  kv("Consent forms signed", consentForms.length);
  kv("Referrals received", referrals.length);
  kv("Privacy acks", privacyAcks.length);
  y += 0.2;
  line();

  // ── Consent forms ──
  if (consentForms.length) {
    section("Consent forms", consentForms.length);
    consentForms.forEach((c) => {
      pageBreakIf(1.2);
      kv("Patient", c.patient_name);
      kv("Signed", `${new Date(c.signed_at).toLocaleString()} · ${c.typed_signature}`);
      kv("Email", `${c.patient_email} · ${c.patient_phone || "—"}`);
      kv("Comms consent", c.agreed_communications ? "Yes" : "No");
      rowDivider();
    });
    line();
  }

  // ── Referrals ──
  if (referrals.length) {
    section("Referrals", referrals.length);
    referrals.forEach((r) => {
      pageBreakIf(1.4);
      kv("Patient", `${r.patient_name} · DOB ${r.patient_dob}`);
      kv("From", `${r.provider_name}${r.provider_credentials ? ", " + r.provider_credentials : ""} — ${r.provider_practice || "—"}`);
      kv("Service", `${r.requested_service} · ${r.urgency}`);
      kv("Status", r.status);
      kv("Received", new Date(r.signed_at).toLocaleString());
      rowDivider();
    });
    line();
  }

  // ── Privacy acks ──
  if (privacyAcks.length) {
    section("Privacy acknowledgments", privacyAcks.length);
    privacyAcks.forEach((p) => {
      pageBreakIf(0.9);
      kv("Patient", p.patient_name);
      kv("Signed", `${new Date(p.signed_at).toLocaleString()} · ${p.typed_signature}`);
      kv("Contact", `${p.patient_email} · ${p.patient_phone || "—"}`);
      rowDivider();
    });
    line();
  }

  // ── Enrollments summary ──
  if (enrollments.length) {
    section("Recent enrollments", enrollments.length);
    enrollments.slice(0, 40).forEach((e) => {
      pageBreakIf(0.8);
      kv("Name", `${e.name} · ${e.status}`);
      kv("Contact", `${e.email} · ${e.phone || "—"}`);
      kv("Reason", (e.reason || "—").slice(0, 220));
      rowDivider();
    });
  }

  pdf.save(`letgo-audit-${new Date().toISOString().slice(0, 10)}.pdf`);
}

// ─────────────────────────────────────────────────────────────
// CSV export helper — turns a list of objects into a downloaded
// spreadsheet-friendly file. Handles commas, quotes and newlines.
// ─────────────────────────────────────────────────────────────
function downloadCsv(filename, rows, headers) {
  if (!rows.length) return;
  const esc = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v).replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };
  const cols = headers || Object.keys(rows[0]);
  const csv = [cols.join(",")]
    .concat(rows.map((r) => cols.map((c) => esc(r[c])).join(",")))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Consent Forms tab ───
const ConsentFormsTab = ({ list, onOpen, onDelete }) => (
  <div className="mt-8">
    {list.length > 0 && (
      <div className="flex justify-end mb-3">
        <button
          onClick={() =>
            downloadCsv(
              `letgo-consent-forms-${new Date().toISOString().slice(0, 10)}.csv`,
              list.map((c) => ({
                id: c.id,
                signed_at: c.signed_at,
                patient_name: c.patient_name,
                patient_dob: c.patient_dob,
                patient_email: c.patient_email,
                patient_phone: c.patient_phone,
                patient_address: c.patient_address,
                emergency_contact_name: c.emergency_contact_name,
                emergency_contact_phone: c.emergency_contact_phone,
                emergency_contact_relationship: c.emergency_contact_relationship,
                insurance_provider: c.insurance_provider,
                insurance_member_id: c.insurance_member_id,
                agreed_terms: c.agreed_terms,
                agreed_communications: c.agreed_communications,
                minor_guardian_name: c.minor_guardian_name,
                typed_signature: c.typed_signature,
              }))
            )
          }
          data-testid="consent-export-csv"
          className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.22em] border border-[#3A5A40] text-[#3A5A40] hover:bg-[#3A5A40] hover:text-white transition-colors"
        >
          Export CSV
        </button>
      </div>
    )}
    {list.length === 0 ? (
      <div className="border border-dashed border-[#D6CFC2] bg-white p-16 text-center">
        <FileSignature className="w-8 h-8 mx-auto text-[#B8B0A1] mb-3" />
        <p className="font-serif italic text-2xl text-[#57534E] mb-2">No signed consents yet.</p>
        <p className="text-sm text-[#86827B]">
          Share <a href="/consent" className="text-[#3A5A40] underline">letgowelllness.com/consent</a> with new patients.
        </p>
      </div>
    ) : (
      <div className="border border-[#E2DCD0] bg-white divide-y divide-[#E2DCD0]">
        {list.map((c) => (
          <div key={c.id} className="p-5 flex items-center justify-between gap-4 hover:bg-[#F4F1EA]/60 transition-colors" data-testid={`consent-row-${c.id}`}>
            <button onClick={() => onOpen(c)} className="flex-1 text-left">
              <p className="font-serif text-lg text-[#1C1917]">{c.patient_name}</p>
              <p className="text-xs text-[#86827B] mt-1">
                {c.patient_email} · {c.patient_phone || "no phone"} · Signed {new Date(c.signed_at).toLocaleString()}
              </p>
              {c.emergency_contact_name && (
                <p className="text-xs text-[#57534E] mt-1">
                  Emergency: {c.emergency_contact_name} ({c.emergency_contact_relationship || "contact"})
                </p>
              )}
            </button>
            <div className="flex items-center gap-3">
              <span className={`text-xs uppercase tracking-[0.22em] px-2 py-1 ${c.agreed_communications ? "bg-[#3A5A40]/10 text-[#3A5A40]" : "bg-[#E2DCD0] text-[#57534E]"}`}>
                {c.agreed_communications ? "Comms OK" : "No comms"}
              </span>
              <button
                onClick={() => onOpen(c)}
                data-testid={`consent-view-${c.id}`}
                className="text-xs uppercase tracking-[0.22em] text-[#3A5A40] hover:underline"
              >
                View
              </button>
              <button
                onClick={() => onDelete(c.id)}
                data-testid={`consent-delete-${c.id}`}
                className="p-2 text-[#86827B] hover:text-[#9F5C45]"
                title="Delete"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const ConsentDetailModal = ({ submission, onClose }) => {
  const c = submission;
  const sigUrl = c.drawn_signature_url
    ? (c.drawn_signature_url.startsWith("http") ? c.drawn_signature_url : `${process.env.REACT_APP_BACKEND_URL}${c.drawn_signature_url}`)
    : null;
  return (
    <div
      className="fixed inset-0 z-[100] bg-black/50 flex items-start md:items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="consent-detail-modal"
    >
      <div
        className="bg-[#FDFBF7] w-full max-w-2xl my-8 border border-[#E2DCD0] shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-[#E2DCD0]">
          <div>
            <p className="overline text-[#9F5C45] mb-1">Consent submission</p>
            <h3 className="font-serif text-2xl text-[#1C1917]">{c.patient_name}</h3>
          </div>
          <button onClick={onClose} className="p-2 text-[#86827B] hover:text-[#1C1917]" data-testid="consent-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-6">
          <Field label="Date of birth" value={c.patient_dob || "—"} />
          <Field label="Address" value={c.patient_address || "—"} />
          <div className="grid md:grid-cols-2 gap-6">
            <Field label="Phone" value={c.patient_phone || "—"} />
            <Field label="Email" value={c.patient_email} />
          </div>
          {c.emergency_contact_name && (
            <div className="border-t border-[#E2DCD0] pt-6">
              <p className="overline text-[#9F5C45] mb-3">Emergency contact</p>
              <p className="font-serif text-lg">{c.emergency_contact_name}</p>
              <p className="text-sm text-[#57534E]">
                {c.emergency_contact_relationship || "Contact"} · {c.emergency_contact_phone || "—"}
              </p>
            </div>
          )}
          {(c.insurance_provider || c.insurance_member_id) && (
            <div className="border-t border-[#E2DCD0] pt-6">
              <p className="overline text-[#9F5C45] mb-3">Insurance</p>
              <p>{c.insurance_provider || "—"} {c.insurance_member_id && `· ${c.insurance_member_id}`}</p>
            </div>
          )}
          {c.minor_guardian_name && (
            <Field label="Guardian (minor)" value={c.minor_guardian_name} />
          )}

          <div className="border-t border-[#E2DCD0] pt-6">
            <p className="overline text-[#9F5C45] mb-3">Agreements</p>
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-[#3A5A40]" />
                Agreed to informed-consent terms
              </li>
              {c.agreed_communications && (
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-[#3A5A40]" />
                  Consent to non-clinical communications
                </li>
              )}
            </ul>
          </div>

          <div className="border-t border-[#E2DCD0] pt-6">
            <p className="overline text-[#9F5C45] mb-3">Signature</p>
            <p className="font-serif italic text-2xl text-[#1C1917] mb-1">{c.typed_signature}</p>
            <p className="text-xs text-[#86827B]">
              Electronically signed {new Date(c.signed_at).toLocaleString()}
            </p>
            {sigUrl && (
              <div className="mt-4 border border-[#E2DCD0] bg-white p-3">
                <img src={sigUrl} alt="Drawn signature" className="max-h-40 mx-auto" data-testid="consent-drawn-sig" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, value }) => (
  <div>
    <p className="overline text-[#9F5C45] mb-1">{label}</p>
    <p className="text-[#1C1917]">{value}</p>
  </div>
);

// ─── Referrals tab ───
const REF_STATUS_COLORS = {
  new: "bg-[#9F5C45]/10 text-[#9F5C45]",
  contacted: "bg-[#3A5A40]/10 text-[#3A5A40]",
  scheduled: "bg-[#3A5A40] text-white",
  archived: "bg-[#E2DCD0] text-[#57534E]",
};

const URGENCY_TONE = {
  urgent: "text-[#9F5C45] font-bold",
  expedited: "text-[#B8862F]",
  routine: "text-[#57534E]",
};

const ReferralsTab = ({ list, onOpen, onDelete, onStatus }) => (
  <div className="mt-8">
    {list.length > 0 && (
      <div className="flex justify-end mb-3">
        <button
          onClick={() =>
            downloadCsv(
              `letgo-referrals-${new Date().toISOString().slice(0, 10)}.csv`,
              list.map((r) => ({
                id: r.id,
                signed_at: r.signed_at,
                status: r.status,
                urgency: r.urgency,
                requested_service: r.requested_service,
                patient_name: r.patient_name,
                patient_dob: r.patient_dob,
                patient_phone: r.patient_phone,
                patient_email: r.patient_email,
                patient_insurance: r.patient_insurance,
                provider_name: r.provider_name,
                provider_credentials: r.provider_credentials,
                provider_practice: r.provider_practice,
                provider_npi: r.provider_npi,
                provider_phone: r.provider_phone,
                provider_fax: r.provider_fax,
                provider_email: r.provider_email,
                provider_address: r.provider_address,
                reason: r.reason,
                diagnosis: r.diagnosis,
                symptoms: r.symptoms,
                clinical_history: r.clinical_history,
                current_medications: r.current_medications,
                prior_treatment: r.prior_treatment,
                signature: r.signature,
              }))
            )
          }
          data-testid="referral-export-csv"
          className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.22em] border border-[#3A5A40] text-[#3A5A40] hover:bg-[#3A5A40] hover:text-white transition-colors"
        >
          Export CSV
        </button>
      </div>
    )}
    {list.length === 0 ? (
      <div className="border border-dashed border-[#D6CFC2] bg-white p-16 text-center">
        <Stethoscope className="w-8 h-8 mx-auto text-[#B8B0A1] mb-3" />
        <p className="font-serif italic text-2xl text-[#57534E] mb-2">No referrals yet.</p>
        <p className="text-sm text-[#86827B]">
          Share <a href="/referral" className="text-[#3A5A40] underline">letgowelllness.com/referral</a> with local PCPs and clinicians.
        </p>
      </div>
    ) : (
      <div className="border border-[#E2DCD0] bg-white divide-y divide-[#E2DCD0]">
        {list.map((r) => (
          <div key={r.id} className="p-5 hover:bg-[#F4F1EA]/60 transition-colors" data-testid={`referral-row-${r.id}`}>
            <div className="flex items-start justify-between gap-4">
              <button onClick={() => onOpen(r)} className="flex-1 text-left">
                <div className="flex items-baseline flex-wrap gap-2 mb-1">
                  <p className="font-serif text-lg text-[#1C1917]">{r.patient_name}</p>
                  <span className={`text-xs uppercase tracking-[0.22em] ${URGENCY_TONE[r.urgency] || URGENCY_TONE.routine}`}>
                    · {r.urgency}
                  </span>
                </div>
                <p className="text-sm text-[#57534E]">
                  {r.requested_service} · DOB {r.patient_dob}
                </p>
                <p className="text-xs text-[#86827B] mt-1">
                  Referred by {r.provider_name}{r.provider_credentials ? `, ${r.provider_credentials}` : ""}
                  {r.provider_practice ? ` (${r.provider_practice})` : ""}
                  {" · "}
                  {new Date(r.signed_at).toLocaleString()}
                </p>
              </button>
              <div className="flex items-center gap-3 flex-shrink-0">
                <span className={`text-xs uppercase tracking-[0.22em] px-2 py-1 ${REF_STATUS_COLORS[r.status] || REF_STATUS_COLORS.new}`}>
                  {r.status}
                </span>
                <button
                  onClick={() => onOpen(r)}
                  data-testid={`referral-view-${r.id}`}
                  className="text-xs uppercase tracking-[0.22em] text-[#3A5A40] hover:underline"
                >
                  View
                </button>
                <button
                  onClick={() => onDelete(r.id)}
                  data-testid={`referral-delete-${r.id}`}
                  className="p-2 text-[#86827B] hover:text-[#9F5C45]"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            {/* Quick status pills */}
            <div className="flex gap-2 mt-3 flex-wrap">
              {["new", "contacted", "scheduled", "archived"].map((s) => (
                <button
                  key={s}
                  onClick={() => onStatus(r.id, s)}
                  disabled={r.status === s}
                  className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 transition-colors ${
                    r.status === s
                      ? "bg-[#3A5A40] text-white opacity-70"
                      : "border border-[#D6CFC2] text-[#57534E] hover:border-[#3A5A40] hover:text-[#3A5A40]"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const ReferralDetailModal = ({ referral, onClose }) => {
  const r = referral;
  return (
    <div
      className="fixed inset-0 z-[100] bg-black/50 flex items-start md:items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="referral-detail-modal"
    >
      <div
        className="bg-[#FDFBF7] w-full max-w-3xl my-8 border border-[#E2DCD0] shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-[#E2DCD0]">
          <div>
            <p className="overline text-[#9F5C45] mb-1">Referral · {r.urgency}</p>
            <h3 className="font-serif text-2xl text-[#1C1917]">{r.patient_name}</h3>
            <p className="text-sm text-[#57534E] mt-1">{r.requested_service}</p>
          </div>
          <button onClick={onClose} className="p-2 text-[#86827B] hover:text-[#1C1917]" data-testid="referral-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-6">
          <div>
            <p className="overline text-[#9F5C45] mb-3">Referring provider</p>
            <p className="font-serif text-lg">
              {r.provider_name}{r.provider_credentials && `, ${r.provider_credentials}`}
            </p>
            <p className="text-sm text-[#57534E]">{r.provider_practice}</p>
            <div className="grid md:grid-cols-2 gap-4 mt-3 text-sm">
              {r.provider_phone && <div><span className="text-[#86827B]">Phone:</span> {r.provider_phone}</div>}
              {r.provider_fax && <div><span className="text-[#86827B]">Fax:</span> {r.provider_fax}</div>}
              {r.provider_email && <div><span className="text-[#86827B]">Email:</span> {r.provider_email}</div>}
              {r.provider_npi && <div><span className="text-[#86827B]">NPI:</span> {r.provider_npi}</div>}
              {r.provider_address && <div className="md:col-span-2"><span className="text-[#86827B]">Address:</span> {r.provider_address}</div>}
            </div>
          </div>

          <div className="border-t border-[#E2DCD0] pt-6">
            <p className="overline text-[#9F5C45] mb-3">Patient</p>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <Field label="DOB" value={r.patient_dob} />
              <Field label="Phone" value={r.patient_phone || "—"} />
              <Field label="Email" value={r.patient_email || "—"} />
              <Field label="Insurance" value={r.patient_insurance || "—"} />
            </div>
          </div>

          <div className="border-t border-[#E2DCD0] pt-6 space-y-4">
            <p className="overline text-[#9F5C45] mb-1">Clinical</p>
            <Field label="Reason for referral" value={r.reason} />
            {r.diagnosis && <Field label="Diagnosis" value={r.diagnosis} />}
            {r.symptoms && <Field label="Symptoms" value={r.symptoms} />}
            {r.clinical_history && <Field label="History" value={r.clinical_history} />}
            {r.current_medications && <Field label="Medications" value={r.current_medications} />}
            {r.prior_treatment && <Field label="Prior treatment" value={r.prior_treatment} />}
          </div>

          <div className="border-t border-[#E2DCD0] pt-6">
            <p className="overline text-[#9F5C45] mb-3">Sign-off</p>
            <p className="font-serif italic text-2xl text-[#1C1917] mb-1">{r.signature}</p>
            <p className="text-xs text-[#86827B]">
              ROI confirmed · Signed {new Date(r.signed_at).toLocaleString()}
            </p>
          </div>

          <div className="border-t border-[#E2DCD0] pt-6 flex flex-wrap gap-3">
            <a
              href={`mailto:${r.provider_email}?${new URLSearchParams({
                subject: `Referral received — ${r.patient_name}`,
                body:
`Dr. ${(r.provider_name || "").split(" ").slice(-1)[0] || r.provider_name},\n\nThank you for referring ${r.patient_name} to Let Go Wellness. This message confirms receipt of your ${r.urgency} referral for ${r.requested_service}.\n\nOur care coordinator will contact the patient within one business day (or same-day for urgent referrals) and will follow up with you directly once the patient is scheduled.\n\nIf you need to reach us in the meantime, please call 203-437-8193 or reply to this email.\n\nWarmly,\nLet Go Wellness Care Coordination\nletgowelllness.com`
              }).toString().replace(/\+/g, "%20")}`}
              data-testid="referral-confirm-receipt"
              className="inline-flex items-center gap-2 px-5 py-3 bg-[#3A5A40] text-[#FDFBF7] hover:bg-[#243B2C] text-xs uppercase tracking-[0.22em] transition-colors"
            >
              <Mail className="w-4 h-4" /> Confirm receipt to referrer
            </a>
            <a
              href={`tel:${(r.patient_phone || "").replace(/[^0-9+]/g, "")}`}
              className="inline-flex items-center gap-2 px-5 py-3 border border-[#3A5A40] text-[#3A5A40] hover:bg-[#3A5A40] hover:text-white text-xs uppercase tracking-[0.22em] transition-colors"
            >
              <Phone className="w-4 h-4" /> Call patient
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Privacy acks tab ───
const PrivacyAcksTab = ({ list, onDelete }) => (
  <div className="mt-8">
    {list.length === 0 ? (
      <div className="border border-dashed border-[#D6CFC2] bg-white p-16 text-center">
        <ShieldCheck className="w-8 h-8 mx-auto text-[#B8B0A1] mb-3" />
        <p className="font-serif italic text-2xl text-[#57534E] mb-2">No privacy acknowledgments yet.</p>
        <p className="text-sm text-[#86827B]">
          Share <a href="/privacy" className="text-[#3A5A40] underline">letgowelllness.com/privacy</a> as part of intake.
        </p>
      </div>
    ) : (
      <div className="border border-[#E2DCD0] bg-white divide-y divide-[#E2DCD0]">
        {list.map((p) => (
          <div key={p.id} className="p-5 flex items-center justify-between gap-4" data-testid={`privacy-row-${p.id}`}>
            <div className="flex-1">
              <p className="font-serif text-lg text-[#1C1917]">{p.patient_name}</p>
              <p className="text-xs text-[#86827B] mt-1">
                {p.patient_email}{p.patient_phone ? ` · ${p.patient_phone}` : ""}
                {p.patient_dob ? ` · DOB ${p.patient_dob}` : ""}
              </p>
              <p className="text-xs text-[#57534E] mt-1">
                Signed <em className="not-italic font-medium">{p.typed_signature}</em> · {new Date(p.signed_at).toLocaleString()}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs uppercase tracking-[0.22em] px-2 py-1 bg-[#3A5A40]/10 text-[#3A5A40]">
                <Check className="inline w-3 h-3 mr-1" /> Ack
              </span>
              <button
                onClick={() => onDelete(p.id)}
                data-testid={`privacy-delete-${p.id}`}
                className="p-2 text-[#86827B] hover:text-[#9F5C45]"
                title="Delete"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);




const ScheduleTab = ({ authHeader }) => {
  const today = startOfDay(new Date());
  const [date, setDate] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState("");

  const dateStr = useMemo(() => format(date, "yyyy-MM-dd"), [date]);

  const fetchDay = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/appointments/day/${dateStr}`, { headers: authHeader() });
      setData(data);
    } catch (e) {
      console.error(e);
      toast.error("Could not load schedule.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDay(); /* eslint-disable-next-line */ }, [dateStr]);

  const block = async (time) => {
    try {
      await axios.post(`${API}/blocked-slots`, { date: dateStr, time, reason: reason || null }, { headers: authHeader() });
      toast.success(`${time} blocked.`);
      fetchDay();
    } catch (e) { toast.error("Could not block slot."); }
  };
  const unblock = async (time) => {
    try {
      await axios.delete(`${API}/blocked-slots`, { params: { date: dateStr, time }, headers: authHeader() });
      toast.success(`${time} re-opened.`);
      fetchDay();
    } catch (e) { toast.error("Could not unblock slot."); }
  };
  const blockWholeDay = async () => {
    try {
      await axios.post(`${API}/blocked-slots`, { date: dateStr, time: null, reason: reason || "Day off" }, { headers: authHeader() });
      toast.success(`${dateStr} marked as closed.`);
      fetchDay();
    } catch (e) { toast.error("Could not block day."); }
  };
  const reopenWholeDay = async () => {
    try {
      await axios.delete(`${API}/blocked-slots`, { params: { date: dateStr }, headers: authHeader() });
      toast.success(`${dateStr} re-opened.`);
      fetchDay();
    } catch (e) { toast.error("Could not re-open day."); }
  };

  return (
    <div className="grid lg:grid-cols-[320px_1fr] gap-4" data-testid="schedule-tab">
      <div className="bg-white border border-[#E2DCD0] p-4 flex flex-col items-center">
        <div className="overline text-[#9F5C45] mb-2 self-start px-2">Pick a day</div>
        <Calendar
          mode="single"
          selected={date}
          onSelect={(d) => d && setDate(d)}
          disabled={(d) => d < today || d > addDays(today, 90)}
          className="rounded-none"
        />
        <div className="w-full mt-4 space-y-3 px-2">
          <input
            className="lgw-field"
            placeholder="Reason (optional — e.g. Lunch, Holiday)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="schedule-reason"
          />
          {data?.day_off ? (
            <button
              onClick={reopenWholeDay}
              data-testid="schedule-reopen-day"
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 btn-primary text-xs tracking-wide"
            >
              <Unlock className="w-3.5 h-3.5" /> Re-open this whole day
            </button>
          ) : data?.open ? (
            <button
              onClick={blockWholeDay}
              data-testid="schedule-block-day"
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 text-xs tracking-wide bg-[#9F5C45] text-[#FDFBF7] hover:bg-[#2C4C3B] transition-colors"
            >
              <Ban className="w-3.5 h-3.5" /> Mark whole day off
            </button>
          ) : null}
        </div>
      </div>

      <div className="bg-white border border-[#E2DCD0] p-6 md:p-8 min-h-[400px]">
        <div className="flex items-baseline justify-between mb-6">
          <div>
            <div className="overline text-[#9F5C45] mb-1">
              <CalendarDays className="w-3.5 h-3.5 inline-block mr-1" />
              {format(date, "EEEE, MMMM d, yyyy")}
            </div>
            <h3 className="font-serif text-2xl text-[#1C1917]">Daily schedule</h3>
          </div>
        </div>

        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#3A5A40]" /></div>
        ) : !data?.open ? (
          <p className="text-[#57534E] italic font-serif text-lg">The clinic is closed on this day (weekly schedule).</p>
        ) : data.day_off ? (
          <p className="text-[#9F5C45] italic font-serif text-lg" data-testid="schedule-day-off">
            Day marked as closed{data.day_off_reason ? ` · ${data.day_off_reason}` : ""}.
          </p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {data.slots.map((s) => {
              const cls =
                s.state === "booked"
                  ? "bg-[#3A5A40] text-[#FDFBF7]"
                  : s.state === "blocked"
                  ? "bg-[#9F5C45] text-[#FDFBF7]"
                  : "bg-[#F4F1EA] text-[#1C1917] hover:bg-[#EAE5D9]";
              const onClick =
                s.state === "available" ? () => block(s.time) :
                s.state === "blocked"   ? () => unblock(s.time) :
                undefined;
              return (
                <button
                  key={s.time}
                  onClick={onClick}
                  disabled={s.state === "booked"}
                  data-testid={`slot-${s.time}-${s.state}`}
                  className={`p-3 text-left transition-colors disabled:cursor-not-allowed ${cls}`}
                  title={s.state === "booked" ? `Booked · ${s.patient || ""}` : s.state === "blocked" ? `Blocked${s.reason ? ` · ${s.reason}` : ""}` : "Click to block"}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-serif text-base">{fmtTime(s.time)}</span>
                    {s.state === "booked" && <Lock className="w-3.5 h-3.5 opacity-80" />}
                    {s.state === "blocked" && <Ban className="w-3.5 h-3.5 opacity-80" />}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.2em] mt-1 opacity-80">
                    {s.state === "booked" ? `Booked · ${(s.patient || "").split(" ")[0] || ""}` :
                     s.state === "blocked" ? "Blocked" : "Open"}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        <div className="flex flex-wrap gap-5 mt-8 pt-6 border-t border-[#E2DCD0] text-xs text-[#57534E]">
          <Legend color="#F4F1EA" border="#E2DCD0" label="Open · click to block" />
          <Legend color="#9F5C45" label="Blocked · click to re-open" />
          <Legend color="#3A5A40" label="Booked patient" />
        </div>
      </div>
    </div>
  );
};

const Legend = ({ color, border, label }) => (
  <span className="inline-flex items-center gap-2">
    <span className="inline-block w-3 h-3" style={{ background: color, border: border ? `1px solid ${border}` : "none" }} />
    {label}
  </span>
);
